<template>
  <v-card flat class="pa-6">
    <create-individual-modal
      v-if="showEditModal && editingEntityType === 'Individual'"
      :initial-data="editingRowData"
      data-only
      :override-individuals-list="importIndividualsListForValidation"
      @edited="handleRowEdited"
      @close="closeEditModal"
    />
    <create-company-modal
      v-if="showEditModal && editingEntityType === 'Company'"
      :initial-data="editingRowData"
      data-only
      @edited="handleRowEdited"
      @close="closeEditModal"
    />
    <create-committee-modal
      v-if="showEditModal && editingEntityType === 'Committee'"
      :initial-data="editingRowData"
      data-only
      :hide-existing-individuals="isCreatingNewCompany"
      @edited="handleRowEdited"
      @close="closeEditModal"
    />
    <h2 class="text-h5 primary-header sans-serif">
      {{ $t('form.import.preview.title') }}
    </h2>
    <v-card-subtitle>
      {{ $t('form.import.preview.subtitle') }}
    </v-card-subtitle>
    <v-card-text>
      <p v-if="hasValidationErrors" class="warning--text mb-4">
        {{ $t('form.import.preview.validationWarning') }} {{ $t('form.import.preview.clickToEdit') }}
      </p>

      <v-tabs v-model="activeTab">
        <v-tab v-for="mapping in activeMappings" :key="mapping.sheetName">
          {{ mapping.sheetName }} ({{ mapping.entityType }})
          <v-chip
            v-if="getInvalidRowCount(mapping) > 0"
            small
            color="warning"
            class="ml-2"
          >
            {{ getInvalidRowCount(mapping) }}
          </v-chip>
        </v-tab>
      </v-tabs>
      <v-tabs-items v-model="activeTab">
        <v-tab-item v-for="mapping in activeMappings" :key="mapping.sheetName">
          <v-data-table
            :headers="getTableHeaders(mapping)"
            :items="getPreviewRows(mapping)"
            :items-per-page="10"
            class="mt-4 preview-table"
            dense
          >
            <template #item="{ item, index }">
              <tr
                :class="{
                  'warning lighten-4': !item.isValid,
                  'cursor-pointer': true
                }"
                @click="openEditModal(mapping, item, index)"
              >
                <td class="sticky-status">
                  <div class="d-flex align-center">
                    <v-icon v-if="item.isValid" small color="success">mdi-check-circle</v-icon>
                    <v-tooltip v-else bottom>
                      <template #activator="{ on, attrs }">
                        <v-icon small color="warning" v-bind="attrs" v-on="on">mdi-alert-circle</v-icon>
                      </template>
                      <span>{{ $t('form.import.preview.missingFields') }}: {{ item.missingFields.join(', ') }}</span>
                    </v-tooltip>
                    <v-icon small class="ml-2 cursor-pointer" @click.stop="openEditModal(mapping, item, index)">mdi-pencil</v-icon>
                    <v-icon small class="ml-2 cursor-pointer" color="error" @click.stop="confirmDeleteRow(mapping, index)">mdi-close</v-icon>
                  </div>
                </td>
                <td v-for="header in getDataHeaders(mapping)" :key="header.value">
                  <span :class="{ 'warning--text font-weight-bold': isMissingRequired(item, header.value) }">
                    {{ getFieldValue(item, header.value) || '-' }}
                  </span>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-tab-item>
      </v-tabs-items>

      <v-divider class="my-6" />

      <v-row>
        <v-col cols="12">
          <h3 class="text-h6 mb-4">{{ $t('form.import.preview.importSummary') }}</h3>
          <v-data-table
            :headers="summaryHeaders"
            :items="summaryItems"
            hide-default-footer
            dense
            class="text-body-2"
          >
            <template v-slot:[`item.validRows`]="{ item }">
              <span class="success--text">{{ item.validRows }}</span>
            </template>
            <template v-slot:[`item.invalidRows`]="{ item }">
              <span :class="{ 'warning--text': item.invalidRows > 0 }">{{ item.invalidRows }}</span>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-card-text>

    <confirmation-modal
      v-if="showDeleteConfirmation"
      action="delete"
      entity-type="settings"
      :force-title="$t('form.import.preview.removeRowTitle')"
      :message="$t('form.import.preview.removeRowMessage')"
      force-icon="delete"
      :image="false"
      @confirm="deleteRow"
      @cancel="cancelDeleteRow"
    />
  </v-card>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import {
  BulkImportWizardColumnMapping,
  BulkImportWizardParsedSheet,
  BulkImportWizardSheetMapping,
  ImportEntityType
} from '@betterboards/shared/types/Import'
import { getFieldDefinitions, getRequiredFields } from '@betterboards/shared/helpers/import'
import { transformToFormData, transformFromFormData } from '@betterboards/shared/helpers/import/transformers'
import { applyMappings, reverseApplyMappings } from '@/helpers/import/fieldMapper'
import { normalizeCommitteeSheet, normalizeMultiColumnCommitteeSheet, detectCommitteeSheetFormat, CommitteeSheetFormat } from '@/helpers/import/normalizeCommitteeSheet'
import CreateIndividualModal from '@/components/individuals/CreateIndividualModal.vue'
import CreateCompanyModal from '@/components/companies/CreateCompanyModal.vue'
import CreateCommitteeModal from '@/components/committees/CreateCommitteeModal.vue'
import ConfirmationModal from '@/components/modals/ConfirmationModal.vue'

interface TableHeader {
  text: string
  value: string
  sortable: boolean
  width?: string
}

interface ValidatedRow extends Record<string, unknown> {
  isValid: boolean
  missingFields: string[]
}

export default Vue.extend({
  name: 'BulkImportPreview',
  components: {
    CreateIndividualModal,
    CreateCompanyModal,
    CreateCommitteeModal,
    ConfirmationModal
  },
  props: {
    parsedSheets: {
      type: Array as PropType<BulkImportWizardParsedSheet[]>,
      required: true
    },
    sheetMappings: {
      type: Array as PropType<BulkImportWizardSheetMapping[]>,
      required: true
    },
    selectedCompanyId: {
      type: String,
      default: ''
    }
  },
  data: () => ({
    activeTab: 0,
    showEditModal: false,
    editingRow: null as ValidatedRow | null,
    editingRowIndex: 0,
    editingSheetName: '',
    editingEntityType: 'Individual' as ImportEntityType,
    showDeleteConfirmation: false,
    deletingRowIndex: -1,
    deletingSheetName: ''
  }),
  computed: {
    activeMappings (): BulkImportWizardSheetMapping[] {
      return this.sheetMappings.filter(m => m.entityType !== null)
    },
    hasValidationErrors (): boolean {
      return this.activeMappings.some(m => this.getInvalidRowCount(m) > 0)
    },
    allRowsValid (): boolean {
      return !this.hasValidationErrors
    },
    isCreatingNewCompany (): boolean {
      return this.activeMappings.some(map => map.entityType === 'Company')
    },
    importIndividualsListForValidation (): Array<{ id: string, preferredName: string }> | null {
      if (!this.isCreatingNewCompany) {
        return null
      }
      const individualMapping = this.activeMappings.find(m => m.entityType === 'Individual')
      if (!individualMapping) {
        return []
      }
      const allRows = this.getAllRows(individualMapping)
      return allRows
        .filter((_, index) => index !== this.editingRowIndex || individualMapping.sheetName !== this.editingSheetName)
        .map((row, index) => ({
          id: `import-${index}`,
          preferredName: (row.preferredName as string) || ''
        }))
        .filter(i => i.preferredName)
    },
    summaryHeaders (): TableHeader[] {
      return [
        { text: this.$t('form.import.preview.entityType') as string, value: 'entityType', sortable: false },
        { text: this.$t('form.import.preview.validRows') as string, value: 'validRows', sortable: false },
        { text: this.$t('form.import.preview.invalidRows') as string, value: 'invalidRows', sortable: false },
        { text: this.$t('form.import.preview.totalRows') as string, value: 'totalRows', sortable: false }
      ]
    },
    summaryItems (): Array<{ entityType: string, validRows: number, invalidRows: number, totalRows: number }> {
      return this.activeMappings.map(mapping => ({
        entityType: mapping.entityType as string,
        validRows: this.getValidRowCount(mapping),
        invalidRows: this.getInvalidRowCount(mapping),
        totalRows: this.getRowCount(mapping)
      }))
    },
    editingRowData (): Record<string, unknown> {
      if (!this.editingRow) {
        return {}
      }

      // Extract entity data (exclude validation metadata)
      const entityData: Record<string, unknown> = {}
      Object.keys(this.editingRow).forEach(key => {
        if (key !== 'isValid' && key !== 'missingFields') {
          entityData[key] = this.editingRow?.[key]
        }
      })

      // Transform to form data format using the entity transformer
      const formData = transformToFormData(entityData, this.editingEntityType)

      // For committees, include all available members from all committees
      if (this.editingEntityType === 'Committee') {
        formData.allAvailableMembers = this.allCommitteeMembers
      }

      return formData
    },
    allCommitteeMembers (): string[] {
      const committeeMapping = this.activeMappings.find(m => m.entityType === 'Committee')
      if (!committeeMapping) {
        return []
      }

      const allRows = this.getAllRows(committeeMapping)
      const allMembers = new Set<string>()

      for (const row of allRows) {
        const members = row.members as string[] | undefined
        if (members && Array.isArray(members)) {
          members.forEach(m => allMembers.add(m))
        }
      }

      return Array.from(allMembers).sort()
    }
  },
  watch: {
    allRowsValid: {
      handler (isValid: boolean) {
        this.$emit('valid', isValid)
      },
      immediate: true
    }
  },
  methods: {
    getFieldValue (item: Record<string, unknown>, fieldKey: string): unknown {
      if (fieldKey.includes('.')) {
        const [parent, child] = fieldKey.split('.')
        const parentObj = item[parent] as Record<string, unknown> | undefined
        return parentObj?.[child]
      }
      return item[fieldKey]
    },
    isEmptyRow (row: Record<string, unknown>): boolean {
      return Object.values(row).every(
        value => value === undefined || value === null || String(value).trim() === ''
      )
    },
    isMappedRowEmpty (row: ValidatedRow, entityType: ImportEntityType): boolean {
      // Exclude validation metadata when checking for empty
      const dataKeys = Object.keys(row).filter(k => k !== 'isValid' && k !== 'missingFields')
      const hasData = dataKeys.some(key => {
        const value = row[key]
        if (value === undefined || value === null) {
          return false
        }
        if (Array.isArray(value)) {
          return value.length > 0
        }
        return String(value).trim() !== ''
      })

      if (!hasData) {
        return true
      }

      // For committees, must have a name to be valid
      if (entityType === 'Committee') {
        const name = row.name
        return !name || String(name).trim() === ''
      }

      return false
    },
    getMissingRequiredFields (row: Record<string, unknown>, entityType: ImportEntityType): string[] {
      const requiredFields = getRequiredFields(entityType)
      return requiredFields
        .filter(f => {
          const value = this.getFieldValue(row, f.key)
          return value === undefined || value === null || value === ''
        })
        .map(f => f.label)
    },
    validateRow (row: Record<string, unknown>, entityType: ImportEntityType): ValidatedRow {
      const missingFields = this.getMissingRequiredFields(row, entityType)
      return {
        ...row,
        isValid: missingFields.length === 0,
        missingFields
      }
    },
    getTableHeaders (mapping: BulkImportWizardSheetMapping): TableHeader[] {
      const statusHeader: TableHeader = {
        text: '',
        value: '_status',
        sortable: false,
        width: '60px'
      }
      return [statusHeader, ...this.getDataHeaders(mapping)]
    },
    getDataHeaders (mapping: BulkImportWizardSheetMapping): TableHeader[] {
      if (!mapping.entityType) {
        return []
      }
      const definitions = getFieldDefinitions(mapping.entityType)
      const mappedFields = mapping.fieldMappings
        .filter(fm => fm.targetField !== null)
        .map(fm => fm.targetField as string)

      const fieldsToShow = mapping.entityType === 'Committee'
        ? [...new Set([...mappedFields, 'type'])]
        : mappedFields

      return definitions
        .filter(d => fieldsToShow.includes(d.key))
        .map(d => ({
          text: d.label + (d.required ? ' *' : ''),
          value: d.key,
          sortable: true
        }))
    },
    getSourceColumnForField (mappings: BulkImportWizardColumnMapping[], targetField: string): string | undefined {
      const mapping = mappings.find(m => m.targetField === targetField)
      return mapping?.sourceColumn
    },
    getNormalizedRows (sheet: BulkImportWizardParsedSheet, mapping: BulkImportWizardSheetMapping): Array<Record<string, unknown>> {
      // Only normalize committee sheets
      if (mapping.entityType !== 'Committee') {
        return sheet.rows
      }

      // Get source column names for name and members fields
      const nameColumn = this.getSourceColumnForField(mapping.fieldMappings, 'name')
      const memberColumn = this.getSourceColumnForField(mapping.fieldMappings, 'members')

      // If we don't have both columns mapped, skip normalization
      if (!nameColumn || !memberColumn) {
        return sheet.rows
      }

      const format = detectCommitteeSheetFormat(sheet.rows, nameColumn, memberColumn, sheet.columns)

      if (format === CommitteeSheetFormat.MultiColumn) {
        return normalizeMultiColumnCommitteeSheet(sheet.rows, nameColumn, memberColumn, sheet.columns)
      }

      if (format === CommitteeSheetFormat.OnePerRow) {
        return normalizeCommitteeSheet(sheet.rows, nameColumn, memberColumn)
      }

      return sheet.rows
    },
    getPreviewRows (mapping: BulkImportWizardSheetMapping): ValidatedRow[] {
      const sheet = this.parsedSheets.find(s => s.name === mapping.sheetName)
      if (!sheet || !mapping.entityType) {
        return []
      }
      const rows = this.getNormalizedRows(sheet, mapping)
      return rows
        .map(row => {
          const mappedRow = applyMappings(row, mapping.fieldMappings, mapping.entityType as ImportEntityType)
          return this.validateRow(mappedRow, mapping.entityType as ImportEntityType)
        })
        .filter(row => !this.isMappedRowEmpty(row, mapping.entityType as ImportEntityType))
        .slice(0, 50)
    },
    getAllRows (mapping: BulkImportWizardSheetMapping): ValidatedRow[] {
      const sheet = this.parsedSheets.find(s => s.name === mapping.sheetName)
      if (!sheet || !mapping.entityType) {
        return []
      }
      const rows = this.getNormalizedRows(sheet, mapping)
      return rows
        .map(row => {
          const mappedRow = applyMappings(row, mapping.fieldMappings, mapping.entityType as ImportEntityType)
          return this.validateRow(mappedRow, mapping.entityType as ImportEntityType)
        })
        .filter(row => !this.isMappedRowEmpty(row, mapping.entityType as ImportEntityType))
    },
    getRowCount (mapping: BulkImportWizardSheetMapping): number {
      return this.getAllRows(mapping).length
    },
    getValidRowCount (mapping: BulkImportWizardSheetMapping): number {
      return this.getAllRows(mapping).filter(r => r.isValid).length
    },
    getInvalidRowCount (mapping: BulkImportWizardSheetMapping): number {
      return this.getAllRows(mapping).filter(r => !r.isValid).length
    },
    isMissingRequired (item: ValidatedRow, fieldKey: string): boolean {
      if (!item.missingFields) {
        return false
      }
      const definitions = getFieldDefinitions(this.activeMappings[this.activeTab]?.entityType as ImportEntityType)
      const field = definitions.find(d => d.key === fieldKey)
      return field ? item.missingFields.includes(field.label) : false
    },
    openEditModal (mapping: BulkImportWizardSheetMapping, row: ValidatedRow, index: number): void {
      this.editingRow = row
      this.editingRowIndex = index
      this.editingSheetName = mapping.sheetName
      this.editingEntityType = mapping.entityType as ImportEntityType
      this.showEditModal = true
    },
    handleRowEdited (updatedData: Record<string, unknown>): void {
      const mapping = this.activeMappings.find(m => m.sheetName === this.editingSheetName)
      if (mapping) {
        // Transform form data back to raw format using the entity transformer
        const transformedData = transformFromFormData(updatedData, this.editingEntityType)

        // Reverse apply field mappings to get original column names
        const reverseMappedData = reverseApplyMappings(updatedData, mapping.fieldMappings)

        // Merge transformed data (for synthetic fields like committeeType) with reverse mapped data
        const mergedData = {
          ...reverseMappedData,
          ...transformedData
        }

        this.$emit('rowUpdated', {
          sheetName: this.editingSheetName,
          rowIndex: this.editingRowIndex,
          updatedData: mergedData
        })
      }
      this.closeEditModal()
    },
    closeEditModal (): void {
      this.editingRow = null
      this.showEditModal = false
    },
    confirmDeleteRow (mapping: BulkImportWizardSheetMapping, index: number): void {
      this.deletingSheetName = mapping.sheetName
      this.deletingRowIndex = index
      this.showDeleteConfirmation = true
    },
    deleteRow (): void {
      if (this.deletingSheetName && this.deletingRowIndex >= 0) {
        this.$emit('rowRemoved', {
          sheetName: this.deletingSheetName,
          rowIndex: this.deletingRowIndex
        })
      }
      this.cancelDeleteRow()
    },
    cancelDeleteRow (): void {
      this.showDeleteConfirmation = false
      this.deletingRowIndex = -1
      this.deletingSheetName = ''
    }
  }
})
</script>

<style lang="scss" scoped>
.cursor-pointer {
  cursor: pointer;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
}

.preview-table {
  ::v-deep {
    .v-data-table__wrapper {
      overflow-x: auto;
    }

    th:first-child,
    .sticky-status {
      position: sticky;
      left: 0;
      background-color: white;
      z-index: 1;
    }

    tr.warning.lighten-4 .sticky-status {
      background-color: #fff8e1;
    }
  }
}
</style>
